import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { IDropdownOption } from '@fluentui/react';
import { Select, Field, FluentProvider, webLightTheme } from '@fluentui/react-components';
import React, { useEffect, useState } from 'react';
import { Controller, FieldValues, UseFormMethods, useWatch } from 'react-hook-form';
import { getDayDropdownOptions, monthDropdownOptions } from '../../../../utils/Helpers';

interface IMonthDayPickerProps {
  dueDateMonth: number;
  dueDateDay: number;
  form: UseFormMethods<FieldValues>;
  dayFormName: string;
  monthFormName: string;
  label: string | JSX.Element;
  dayLabel?: JSX.Element;
  required: boolean;
  disabled?: boolean;
}

const MonthDayPicker = ({
  dueDateMonth,
  dueDateDay,
  form,
  dayFormName,
  monthFormName,
  label,
  dayLabel,
  required,
  disabled,
}: IMonthDayPickerProps): JSX.Element => {
  const { errors, control, setValue, getValues } = form;
  const isCustomLabel = label && typeof label !== 'string';

  const watchDueDateMonth = useWatch({
    control,
    name: monthFormName,
    defaultValue: monthDropdownOptions.find((month) => month.key === Number(dueDateMonth))?.key,
  });

  const [daysDropDownValues, setDaysDropDownValues] = useState<IDropdownOption[]>(
    getDayDropdownOptions(watchDueDateMonth as number),
  );

  const currentDay = daysDropDownValues.find((day) => day.key === Number(dueDateDay));

  useEffect(() => {
    const days = getDayDropdownOptions(watchDueDateMonth as number);

    if (getValues(dayFormName) && !days.find((day) => day.key === getValues(dayFormName))) {
      setValue(dayFormName, null);
    }

    setDaysDropDownValues(days);
  }, [watchDueDateMonth]);

  const validateMonth = (): string => {
    if (!getValues(monthFormName) && required) {
      return `${label} Month is required`;
    }
    return null;
  };

  const validateDay = (): string => {
    if (!getValues(dayFormName) && required) {
      return 'Day is required';
    }
    return null;
  };

  const dayDropDownStyles = { width: 100 };
  const monthDropDownStyles = { width: 300 };

  return (
    <FluentProvider theme={webLightTheme}>
      <Stack horizontal tokens={{ childrenGap: 10 }} wrap>
        <Controller
          render={() => (
            <div>
              {isCustomLabel && label}
              <Field
                label={isCustomLabel ? undefined : `${label} Month`}
                validationMessage={errors[monthFormName]?.message}
              >
                <Select
                  value={watchDueDateMonth?.toString() ?? ''}
                  required={required}
                  onChange={(event: React.ChangeEvent<HTMLSelectElement>): void => {
                    setValue(monthFormName, event.target.value ? Number(event.target.value) : null);
                  }}
                  style={monthDropDownStyles}
                >
                  <option value="" disabled>
                    Select a month
                  </option>
                  {monthDropdownOptions.map((option) => (
                    <option key={option.key.toString()} value={option.key.toString()}>
                      {option.text}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          )}
          name={monthFormName}
          control={control}
          defaultValue={dueDateMonth !== undefined ? dueDateMonth : null}
          value={watchDueDateMonth}
          rules={{
            validate: validateMonth,
          }}
        />
        <Controller
          render={() => (
            <div>
              {dayLabel && dayLabel}
              <Field
                label={dayLabel ? undefined : 'Day'}
                validationMessage={errors[dayFormName]?.message}
              >
                <Select
                  value={getValues(dayFormName)?.toString() ?? ''}
                  required={required}
                  onChange={(event: React.ChangeEvent<HTMLSelectElement>): void => {
                    setValue(dayFormName, event.target.value ? Number(event.target.value) : null);
                  }}
                  style={dayDropDownStyles}
                  disabled={disabled}
                >
                  <option value="" disabled>
                    Select a day
                  </option>
                  {daysDropDownValues.map((option) => (
                    <option key={option.key.toString()} value={option.key.toString()}>
                      {option.text}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          )}
          name={dayFormName}
          control={control}
          defaultValue={dueDateDay !== undefined ? dueDateDay : null}
          value={currentDay?.key}
          rules={{
            validate: validateDay,
          }}
        />
      </Stack>
    </FluentProvider>
  );
};

export default MonthDayPicker;
